import { Menu, Tooltip } from '@chakra-ui/react';
import type { Editor } from '@tiptap/react';
import {
  ToolbarSelectMenuItem,
  ToolbarSelectMenuList,
  ToolbarSelectTrigger,
} from './EditorToolbarSelect';

// Presets mirror the @kula-ai/components Email editor so both template editors
// offer the same sizes. Stored as px on the `textStyle` mark.
export const FONT_SIZE_PRESETS = [14, 16, 18, 20, 24, 28] as const;
export const DEFAULT_FONT_SIZE = 14;

const nearestPreset = (value: number) =>
  FONT_SIZE_PRESETS.reduce(
    (best, cur) =>
      Math.abs(cur - value) < Math.abs(best - value) ? cur : best,
    DEFAULT_FONT_SIZE as number
  );

const getCurrentSize = (editor?: Editor) => {
  const raw = editor?.getAttributes('textStyle')?.fontSize as
    | string
    | undefined;
  if (raw?.endsWith('px')) {
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n)) {
      return nearestPreset(n);
    }
  }
  return DEFAULT_FONT_SIZE;
};

type EditorFontSizeProps = {
  editor?: Editor;
  isTextSelected?: boolean;
};

export const EditorFontSize = ({ editor }: EditorFontSizeProps) => {
  if (!editor) {
    return null;
  }

  const currentSize = getCurrentSize(editor);

  const applySize = (size: number) => {
    editor.chain().focus().setFontSize(`${size}px`).run();
  };

  return (
    <Menu gutter={2} placement="bottom-start" matchWidth>
      <Tooltip
        label="Font size"
        arrowSize={8}
        hasArrow
        placement="top"
        variant="ats-primary"
        borderRadius="4px"
      >
        <ToolbarSelectTrigger
          minW="72px"
          data-test-id="editor-toolbar-fontSize-button"
        >
          {/* kula labels the sizes in `pt` to match design copy. */}
          {`${currentSize}pt`}
        </ToolbarSelectTrigger>
      </Tooltip>
      <ToolbarSelectMenuList>
        {FONT_SIZE_PRESETS.map((size) => (
          <ToolbarSelectMenuItem
            key={size}
            isSelected={size === currentSize}
            onClick={() => applySize(size)}
            data-test-id={`editor-toolbar-fontSize-${size}`}
          >
            {size}
          </ToolbarSelectMenuItem>
        ))}
      </ToolbarSelectMenuList>
    </Menu>
  );
};
